import React from 'react'
import type { PurchaseOrder } from '@ztechapps/common.contract'
import { useAiChatContext } from '../../ai-chat/Context'
import { EntityCreatedMessage } from '../EntityCreatedMessage'
import type { ProductProposal } from '../../CreateProductProposalCard'
import { PurchaseOrderDraftCard, type PurchaseOrderDraft } from './PurchaseOrderDraftCard'

/**
 * Wire format for the `createPurchaseOrder` MCP response variants. Must
 * stay in sync with the backend tool — the strings round-trip through the
 * tool result envelope unchanged.
 */
export enum PurchaseOrderResponseKindEnum {
  Created = 'purchase-order-created',
  Draft = 'purchase-order-draft',
}

export type PurchaseOrderCreatedMessageProps = {
  /** `Created` payload — present when the PO was committed inline. */
  kind?: PurchaseOrderResponseKindEnum
  purchaseOrder?: PurchaseOrder
  fileAttached?: boolean
  /** `Draft` payload — present when at least one line item carries the inline-create triple. */
  purchaseOrderDraft?: PurchaseOrderDraft
  inlineProductProposals?: ProductProposal[]
}

/**
 * Dispatches between the two `createPurchaseOrder` MCP response variants:
 *
 *   - `Created` — the legacy immediate-create path. Renders the existing
 *      "PO Created" confirmation message with the per-surface
 *      `purchaseOrderUrl` resolved out-of-band.
 *
 *   - `Draft` — the propose-and-confirm path. Renders the PO draft summary
 *      plus a nested `CreateProductProposalGroup` for the inline product
 *      proposals. "Create PO" is gated until every proposal is satisfied
 *      (either committed via the engine or swapped for an existing
 *      product) and submits the PO with `inlineProposalRef` swapped for
 *      the resolved `productId`.
 */
export const PurchaseOrderCreatedMessage = ({
  kind,
  purchaseOrder,
  fileAttached,
  purchaseOrderDraft,
  inlineProductProposals,
}: PurchaseOrderCreatedMessageProps) => {
  const { purchaseOrderUrl } = useAiChatContext()

  // Explicit kind dispatch wins. If kind is missing, fall through to the
  // legacy detection: `purchaseOrder` present → created, else draft.
  const isDraft = kind === PurchaseOrderResponseKindEnum.Draft || (!purchaseOrder && !!purchaseOrderDraft)

  if (isDraft && purchaseOrderDraft) {
    return (
      <PurchaseOrderDraftCard
        purchaseOrderDraft={purchaseOrderDraft}
        inlineProductProposals={inlineProductProposals ?? []}
      />
    )
  }

  const code = purchaseOrder?.code ?? ''
  return (
    <EntityCreatedMessage
      label="PO Created:"
      code={code}
      ariaLabel={`View purchase order ${code}`}
      url={purchaseOrder ? purchaseOrderUrl?.(purchaseOrder) : undefined}
      fileAttached={fileAttached ?? (purchaseOrder as { fileAttached?: boolean } | undefined)?.fileAttached}
    />
  )
}
